import React from 'react';
import {
  VictoryAxis,
  VictoryChart,
  VictoryErrorBar,
  VictoryScatter,
  VictoryLine,
  VictoryTheme,
} from 'victory';

const SimpleHealthChart = ({ 
  dataType, 
  unit, 
  data, 
  color = "#3B82F6",
  height = 200,
  width = 250,
  showErrorBars = true 
}) => {
  // Transform the data object into Victory chart format
  const transformData = () => {
    if (!data || typeof data !== 'object') return [];
    
    // Get all dates and sort them
    const dates = Object.keys(data).sort();
    const chartData = [];
    
    dates.forEach((dateStr, index) => {
      const dateData = data[dateStr];
      const date = new Date(dateStr);
      
      if (dateData && dateData.avg !== null && dateData.avg !== undefined) {
        // Data exists for this date
        const dataPoint = {
          x: index + 1,
          y: parseFloat(dateData.avg),
          min: dateData.min !== null && dateData.min !== undefined ? parseFloat(dateData.min) : null,
          max: dateData.max !== null && dateData.max !== undefined ? parseFloat(dateData.max) : null,
          hasData: true,
          date: date
        };
        chartData.push(dataPoint);
      } else {
        // No data for this date - create empty data point
        chartData.push({
          x: index + 1,
          y: null,
          min: null,
          max: null,
          hasData: false,
          date: date
        });
      }
    });
    
    return chartData;
  };

  const chartData = transformData();
  
  // Get date labels for x-axis
  const dateLabels = Object.keys(data || {}).sort().map(dateStr => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  });

  // Format y-axis labels without units
  const formatYAxis = (t) => {
    if (unit === 'steps') {
      return `${(t/1000).toFixed(0)}k`;
    } else {
      return `${t}`;
    }
  };

  if (chartData.length === 0) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="text-center text-gray-500">
          <p>No data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      <VictoryChart
        theme={VictoryTheme.material}
        domainPadding={20}
        height={height}
        width={width}
        padding={{ top: 20, bottom: 40, left: 50, right: 20 }}
        domain={{
          y: dataType === 'heartrate' ? [40, 120] :
             dataType === 'hrv' ? [0, 100] :
             dataType === 'step' ? [0, 15000] :
             [0, 100]
        }}
      >
        {/* X-Axis */}
        <VictoryAxis
          dependentAxis={false}
          tickFormat={(t) => {
            return dateLabels[t - 1] || '';
          }}
          style={{
            tickLabels: { fontSize: 12, padding: 5, fontWeight: 'bold' }
          }}
        />
        
        {/* Y-Axis */}
        <VictoryAxis
          dependentAxis={true}
          tickFormat={formatYAxis}
          style={{
            tickLabels: { fontSize: 12, padding: 5, fontWeight: 'bold' }
          }}
        />
        
        {/* Line Chart */}
        <VictoryLine
          data={chartData.filter(d => d.hasData)}
          style={{
            data: { stroke: color, strokeWidth: 2 }
          }}
        />
        
        {/* Scatter Points */}
        <VictoryScatter
          data={chartData.filter(d => d.hasData)}
          size={3}
          style={{
            data: { fill: color }
          }}
        />
        
        {/* Error Bars - Using VictoryErrorBar with center point */}
        {showErrorBars && (
          (() => {
            const errorData = chartData.filter(d => d.hasData && d.min !== null && d.max !== null);
            
            // Create data with center point as y value
            const errorBarData = errorData.map(datum => ({
              x: datum.x,
              y: (datum.min + datum.max) / 2, // Center point
              min: datum.min,
              max: datum.max,
              errorY: (datum.max - datum.min) / 2 // Half range
            }));
            
            return (
              <VictoryErrorBar
                data={errorBarData}
                x="x"
                y="y"
                errorX={0}
                errorY="errorY"
                style={{
                  data: { 
                    stroke: color, 
                    strokeWidth: 2,
                    strokeLinecap: "round"
                  }
                }}
              />
            );
          })()
        )}
      </VictoryChart>
    </div>
  );
};

export default SimpleHealthChart; 